/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {FC, $Graphics, getProp, patchEvents} from '@gongxigames/framework/core/fragments/BuilderY';
import {Store} from '@gongxigames/framework/core/fragments/store/Store';
import {Graphics as PixiGraphics} from 'pixi.js';
import {RESPONSIVE_CHANGE_EVENT} from '@gongxigames/framework/core/fragments/display/responsive';
import {Size2D} from '@gongxigames/framework/core/types';
import {ExtendGraphicsProps} from './Graphics';

export type GraphicsBoxProps = ExtendGraphicsProps<{
    /**
     * update box size according to responsive configuration
     */
    responsiveBoxSize?: boolean;

    /**
     * box width if responsiveBoxSize is not set to true
     */
    boxWidth?: number;

    /**
     * box height if responsiveBoxSize is not set to true
     */
    boxHeight?: number;

    /**
     * The X offset for draw rect function
     */
    boxX?: number;

    /**
     * The Y offset for draw rect function
     */
    boxY?: number;

    /**
     * Box fill color
     */
    boxColor?: number;

    /**
     * Box fill alpha value
     */
    boxAlpha?: number;

    /**
     * if set corners will be rounded accordingly
     */
    boxBorderRadius?: number;

    /**
     * Box outline color value
     */
    boxOutlineColor?: number;

    /**
     * Box outline alpha value
     */
    boxOutlineAlpha?: number;

    /**
     * Box outline thickness
     */
    boxOutlineWidth?: number;

    /**
     * Box outline offset
     */
    boxOutlineAlignment?: number;
}>;

/**
 * Simple component to draw colored boxes.
 * ```tsx
 * <$GraphicsBox
 *     responsiveBoxSize
 *     boxColor={0xff00ff}
 *     renderable={false}
 *     boxWidth={100}
 *     boxHeight={100}
 *     boxX={0}
 *     boxY={0}
 *     interactive
 *     buttonMode
 *     events={{
 *         pointertap: () => uiMainScreenEmitter.emit('PRESSED_BET_SELECT')
 *     }}
 * />
 * ```
 */
export const $GraphicsBox: FC<GraphicsBoxProps> = (props) => {
    const graphics = props.instance ?? new PixiGraphics();
    const responsiveSizeSignal = Store.signal<Size2D>({width: 0, height: 0});

    graphics.on(RESPONSIVE_CHANGE_EVENT, size => {
        responsiveSizeSignal.value = {...size};
    });

    const off = Store.effect(() => {
        const responsiveBoxSize = getProp(props.responsiveBoxSize);
        const boxWidth = getProp(props.boxWidth, 0);
        const boxHeight = getProp(props.boxHeight, 0);
        const boxX = getProp(props.boxX, 0);
        const boxY = getProp(props.boxY, 0);
        const boxColor = getProp(props.boxColor);
        const boxAlpha = getProp(props.boxAlpha);
        const boxBorderRadius = getProp(props.boxBorderRadius);
        const boxOutlineColor = getProp(props.boxOutlineColor);
        const boxOutlineAlpha = getProp(props.boxOutlineAlpha);
        const boxOutlineWidth = getProp(props.boxOutlineWidth, 0);
        const boxOutlineAlignment = getProp(props.boxOutlineAlignment);

        let width;
        let height;

        if (responsiveBoxSize) {
            width = responsiveSizeSignal.value.width;
            height = responsiveSizeSignal.value.height;
        } else {
            width = boxWidth;
            height = boxHeight;
        }

        graphics
            .clear()
            .beginFill(boxColor, boxAlpha)
            .lineStyle(boxOutlineWidth, boxOutlineColor, boxOutlineAlpha, boxOutlineAlignment);

        boxBorderRadius ?
            graphics.drawRoundedRect(boxX, boxY, width, height, boxBorderRadius) :
            graphics.drawRect(boxX, boxY, width, height);

        graphics.endFill();
    });

    props.events ??= {};
    patchEvents(props.events, {destroyed: off});

    return <$Graphics instance={graphics} {...props}/>;
};